import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {DistanceFrom} from 'components/DistanceFrom'
import {showOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {
  getDistanceFromTarget,
  getSearchType
} from 'modules/sapiSearch/selectors'
import {SearchType} from 'modules/sapiSearch/utils'
import {getIsStreetViewEnabled} from 'utils/map'

import {Button} from '@daedalus/atlas/Button'
import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {ContentWrapper} from '@daedalus/atlas/helpers/ContentWrapper'
import {NoStyleButton} from '@daedalus/atlas/helpers/NoStyleButton'
import {Stack} from '@daedalus/atlas/helpers/Stack'
import {Icon} from '@daedalus/atlas/Icon'
import {useDispatchTeamEvent} from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'

import {LocationSummaryLoading} from './LocationSummaryLoading'
import {
  LocationSummaryWrapper,
  MapLink,
  MobileWrapper,
  PlaceNameWrapper,
  StreetViewText,
  Wrapper
} from './styles'

interface Props {
  hotel?: ContentHotel
  onLocationSummaryClick: () => void
}

export const LocationSummary: React.FC<Props> = ({
  hotel,
  onLocationSummaryClick
}) => {
  const {isMobile} = useDeviceLayout()
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const distanceFromTarget = useSelector(state =>
    getDistanceFromTarget(state, hotel?.objectID)
  )

  const searchType = useSelector(getSearchType)

  const isStreetViewEnabled = getIsStreetViewEnabled(hotel?.country)

  const handleStreetViewClick = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsStreetView,
        action: Action.Clicked,
        component: 'locationSummary'
      })
    )

    dispatch(
      showOverlay({
        type: OverlayType.StreetView,
        params: {
          hotelId: hotel.objectID
        }
      })
    )
  }, [hotel?.objectID, dispatch, dispatchTeamEvent])

  // Show location summary loading
  if (!hotel) return <LocationSummaryLoading />

  if (isMobile) {
    return (
      <MobileWrapper>
        <LocationSummaryWrapper>
          <Stack space="s200">
            <NoStyleButton data-id="Location" onClick={onLocationSummaryClick}>
              <PlaceNameWrapper variant="bodyXS">
                <Icon size="sm" name="Pin" />
                <span>
                  {hotel.placeDisplayName}
                  &nbsp;·&nbsp;
                  <MapLink>
                    <FormattedMessageWrapper id="map" defaultMessage="Map" />
                  </MapLink>
                </span>
              </PlaceNameWrapper>
            </NoStyleButton>
            {searchType !== SearchType.Area && Boolean(distanceFromTarget) && (
              <div>
                <PlaceNameWrapper variant="bodyXS">
                  <DistanceFrom distance={distanceFromTarget} />
                </PlaceNameWrapper>
              </div>
            )}
          </Stack>
          {isStreetViewEnabled && isMobile && (
            <ContentWrapper paddingLeft="s400">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleStreetViewClick}
                iconStart={
                  <Icon name="Person360" colorPath="content.neutral.c950" />
                }
              >
                <StreetViewText variant="bodyXS">
                  <FormattedMessageWrapper
                    id="streetView.CTA"
                    defaultMessage="Street view"
                  />
                </StreetViewText>
              </Button>
            </ContentWrapper>
          )}
        </LocationSummaryWrapper>
      </MobileWrapper>
    )
  }

  return (
    <Wrapper>
      <NoStyleButton data-id="Location" onClick={onLocationSummaryClick}>
        <PlaceNameWrapper variant="bodyS">
          <Icon size="sm" name="Pin" />
          <span>{hotel.placeDisplayName}</span>
          <span>·</span>
          <MapLink>
            <FormattedMessageWrapper id="map" defaultMessage="Map" />
          </MapLink>
        </PlaceNameWrapper>
      </NoStyleButton>
      {searchType !== SearchType.Area && Boolean(distanceFromTarget) && (
        <div>
          <PlaceNameWrapper variant="bodyXS">
            <DistanceFrom distance={distanceFromTarget} />
          </PlaceNameWrapper>
        </div>
      )}
    </Wrapper>
  )
}
